// A BCP-47 language tag, validated by GRAMMAR rather than an allowlist.
//
// Two consumers set the language the model writes in — `.maintainer.yml`'s
// `tone.language` and `review.language` (`@developerz/policy`), and the
// `.coderabbit.yaml` translation (`@developerz/review`) — so the shape lives
// here, where both already depend, rather than being restated in each.
//
// It exists because these were `z.string().min(2)`: an open string that steers
// model output is a field a `.maintainer.yml` can smuggle prose through. But
// the fix has to be a SHAPE, not a list of favourites. `.maintainer.yml` is a
// file already committed in customers' repos, so a closed enum of the ~70 tags
// we happened to think of turns every other real tag — `uk-UA`, `el`, `ro`,
// `bg`, `fa`, `ta`, `sw`, `en-NZ`, `zh-Hant-TW` — into a policy that parsed
// yesterday and refuses today, and leaves a list somebody has to keep current
// forever. A grammar accepts every well-formed tag, needs no maintenance, and
// still admits nothing that reads as an instruction.

import { z } from 'zod';

/**
 * `language-script-region-variant` — the subtags anyone writes in practice:
 * a 2-8 alpha primary subtag, an optional 4-alpha script, an optional 2-alpha
 * or 3-digit region, then registered variants. Case-insensitive, as BCP-47 is.
 *
 * Deliberately NOT the full RFC 5646 grammar: extlangs, extensions (`-u-…`)
 * and private use (`-x-…`) are not something a language field for prose output
 * needs, and every subtag admitted here is one more thing that reaches a
 * prompt. Widen it when a real config needs it, not before.
 */
export const BCP47_TAG =
  /^[A-Za-z]{2,8}(-[A-Za-z]{4})?(-([A-Za-z]{2}|[0-9]{3}))?(-([A-Za-z0-9]{5,8}|[0-9][A-Za-z0-9]{3}))*$/;

/**
 * Bounded at 35 — the length the review side always carried. The regex alone
 * bounds each subtag but not how many variants may follow, and an unbounded
 * match is still an unbounded string at a trust boundary.
 */
export const Bcp47LanguageSchema = z
  .string()
  .max(35)
  .regex(BCP47_TAG, 'must be a BCP-47 language tag, e.g. "en", "en-GB", "pt-BR", "zh-Hant-TW"');
